// src/components/ui/dashboard/grades-chart.tsx
"use client"

import { useEffect, useState } from "react"
import { TrendingUp, Upload, BarChart2 } from "lucide-react"
import { useNavigate } from "react-router"

interface GradeData {
  subject: string
  percentage: number
  color?: string
}

interface GradesChartProps {
  grades?: GradeData[]
}

// Static full class strings — Tailwind JIT requires complete strings at build time
const PALETTE = [
  { bar: "bg-indigo-500",  label: "text-indigo-600" },
  { bar: "bg-teal-500",    label: "text-teal-600"   },
  { bar: "bg-amber-500",   label: "text-amber-600"  },
  { bar: "bg-rose-500",    label: "text-rose-600"   },
  { bar: "bg-violet-500",  label: "text-violet-600" },
]

function GradesEmptyState() {
  const navigate = useNavigate()
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-800">Grades</h3>
        <BarChart2 className="w-4 h-4 text-slate-300" />
      </div>
      <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center">
          <Upload className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700">No results uploaded yet</p>
          <p className="text-xs text-slate-400 mt-0.5">Upload your academic results to see your grades here.</p>
        </div>
        <button
          onClick={() => navigate("/profile")}
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all"
        >
          <Upload className="w-3.5 h-3.5" />
          Upload Results
        </button>
      </div>
    </div>
  )
}

export function GradesChart({ grades }: GradesChartProps) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  if (!grades || grades.length === 0) return <GradesEmptyState />

  const avg = Math.round(grades.reduce((s, g) => s + g.percentage, 0) / grades.length)

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-bold text-slate-800">Grades</h3>
        <div className="flex items-center gap-1.5 bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">
          <TrendingUp className="w-3.5 h-3.5" />
          Avg {avg}%
        </div>
      </div>

      {/* Bar chart */}
      <div className="flex items-end justify-around gap-2 h-40">
        {grades.map((grade, i) => {
          const p = PALETTE[i % PALETTE.length]
          const heightPct = mounted ? grade.percentage : 0
          return (
            <div key={grade.subject} className="flex flex-col items-center gap-1 flex-1 min-w-0">
              <span className={`text-[11px] font-black leading-none ${p.label}`}>
                {grade.percentage}%
              </span>
              <div className="w-full flex justify-center items-end" style={{ height: 120 }}>
                <div
                  className={`w-8 rounded-t-2xl ${p.bar} transition-all duration-700 ease-out`}
                  style={{
                    height: `${heightPct}%`,
                    minHeight: 4,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  }}
                />
              </div>
              <span className="text-[10px] font-semibold text-slate-500 text-center leading-tight truncate w-full">
                {grade.subject}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
